/**
 * F 117 Cut 2 — Block[] -> markdown serialiser.
 *
 * Per F 115's measurements, BlockNote's `blocksToMarkdownLossy` cannot be
 * wrapped naively:
 *
 *   1. It is a METHOD on a live editor instance, not a pure function —
 *      a batch migration would need an editor per document, and the
 *      editor needs a DOM.
 *   2. It THROWS on any block type absent from its schema. F 115 measured
 *      that FCr's own legacy names (todo, toggle, code, bulleted-list,
 *      numbered-list) and `callout` all throw.
 *   3. It drops `underline` and `textColor` marks SILENTLY — a migration
 *      that reports success while deleting formatting is the worst
 *      failure mode available.
 *
 * So this module does NOT wrap BlockNote. It is a self-contained
 * serialiser over the BlockNote block vocabulary (F 116) plus the
 * F 116 legacy aliases, and it reports what it lost.
 *
 * Guarantees:
 *   - Never throws. Any unexpected input degrades to plain text.
 *   - Never returns an empty string for non-empty input.
 *   - Reports every lossy case as a Drop, per document.
 *
 * Cut 3 will add paste normalisation + emoji/icon config. Cut 1
 * (the renderer) is separate and lands after this.
 */

import {
  type BlockType,
  type LegacyBlockType,
  LEGACY_BLOCK_TYPE_ALIASES,
  normaliseBlockType,
} from '../../types/models/Block';

// ─── Types ──────────────────────────────────────────────────────────────────

/** Why content could not be represented faithfully in markdown. */
export type DropReason =
  /** `underline` has no markdown equivalent. */
  | 'underline-mark-unsupported'
  /** `textColor` / FCr `color` has no markdown equivalent. */
  | 'text-colour-unsupported'
  /** Nested children flatten — markdown has no portable nesting. */
  | 'nested-children-flattened'
  /** Legacy name mapped to a canonical type (informational). */
  | 'legacy-type-alias-applied'
  /** `callout` has no canonical node; degraded to a quote. */
  | 'callout-degraded-to-quote'
  /** Unknown type; degraded to paragraph. */
  | 'unknown-type-degraded-to-paragraph'
  /** Image with no URL — emitted as a placeholder line. */
  | 'image-without-url'
  /** A block threw during serialisation; its text survived as plain text. */
  | 'block-serialisation-failed';

export interface Drop {
  /** The block type the loss applies to (legacy name if it was an alias). */
  blockType: string;
  /** Machine-readable reason. */
  reason: DropReason;
  /** Human-readable one-liner, safe to show in a UI notice. */
  message: string;
  /** How many occurrences in this document. */
  count: number;
}

export interface SerializeResult {
  /** Canonical markdown. Never empty when `blocks` was non-empty. */
  markdown: string;
  /** Every lossy conversion, aggregated. Empty array = lossless. */
  drops: Drop[];
}

/** Drop-record shape persisted alongside the markdown. */
export interface MarkdownSidecar {
  version: 1;
  /** The canonical markdown. */
  markdown: string;
  /** Loss report, or null when the conversion was lossless. */
  drops: Drop[] | null;
  /** Source block count, so a reader can spot truncation. */
  blockCount: number;
  /** ISO timestamp of serialisation. */
  serialisedAt: string;
}

// ─── Drop accumulator ───────────────────────────────────────────────────────

class DropLog {
  private readonly byKey = new Map<string, Drop>();

  add(blockType: string, reason: DropReason, message: string): void {
    const key = `${reason}::${blockType}`;
    const existing = this.byKey.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      this.byKey.set(key, { blockType, reason, message, count: 1 });
    }
  }

  list(): Drop[] {
    return Array.from(this.byKey.values());
  }
}

// ─── Inline marks ───────────────────────────────────────────────────────────

interface InlineLike {
  type?: string;
  text?: string;
  href?: string;
  styles?: Record<string, unknown>;
}

/**
 * Serialise inline content to markdown, recording unsupported marks.
 * Never throws; a node that blows up contributes its raw text.
 */
function renderInline(content: unknown, drops: DropLog, blockType: string): string {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return String(content);

  let out = '';
  for (const node of content as InlineLike[]) {
    if (node == null) continue;
    if (typeof node === 'string') {
      out += node;
      continue;
    }
    const text = typeof node.text === 'string' ? node.text : '';
    const styles = node.styles ?? {};

    // Escape before wrapping, so * and _ inside text don't become marks.
    const esc = escapeMd(text);

    if (node.type === 'link' || styles.link) {
      const href = String((node as any).href ?? styles.link ?? '');
      out += `[${esc}](${href})`;
      continue;
    }

    let s = esc;
    if (styles.code) s = `\`${s}\``;
    if (styles.bold) s = `**${s}**`;
    if (styles.italic) s = `*${s}*`;
    if (styles.strike) s = `~~${s}~~`;

    // F 115: these two vanish silently in BlockNote. We report them.
    if (styles.underline) {
      drops.add(
        blockType,
        'underline-mark-unsupported',
        'Underline formatting has no markdown equivalent and was removed.',
      );
    }
    if (styles.textColor || styles.color) {
      drops.add(
        blockType,
        'text-colour-unsupported',
        'Text colour has no markdown equivalent and was removed.',
      );
    }
    out += s;
  }
  return out;
}

function escapeMd(s: string): string {
  return s.replace(/([\\`*_[\]])/g, '\\$1');
}

// ─── Block-level serialisation ──────────────────────────────────────────────

interface BlockLike {
  id?: string;
  type?: string;
  props?: Record<string, unknown>;
  content?: unknown;
  children?: BlockLike[];
}

function serialiseChildren(
  children: BlockLike[] | undefined,
  drops: DropLog,
  depth: number,
): string {
  if (!Array.isArray(children) || children.length === 0) return '';
  // Markdown has no portable block nesting — CommonMark list nesting is
  // indentation-based and does not survive a paste through a plain textarea.
  // F 115 measured BlockNote flattening children to top level; we do the
  // same and REPORT it at every depth, not just deep ones. A child that
  // appears at top level is a structural change, however shallow.
  drops.add(
    '*',
    'nested-children-flattened',
    depth === 0
      ? 'Nested blocks were written at top level; their parent-child relationship is not in the markdown.'
      : 'Deeply nested blocks were flattened to top level.',
  );
  return children.map((c) => serialiseBlock(c, drops, 0)).filter(Boolean).join('\n\n');
}

function serialiseBlock(block: BlockLike, drops: DropLog, depth: number): string {
  if (block == null) return '';

  const rawType = String(block.type ?? 'paragraph');
  const props = block.props ?? {};

  // Legacy name? Report the alias application once, then map forward.
  let type: string = rawType;
  if ((LEGACY_BLOCK_TYPE_ALIASES as Record<string, string>)[rawType]) {
    const mapped = (LEGACY_BLOCK_TYPE_ALIASES as Record<string, string>)[rawType];
    if (rawType === 'callout') {
      drops.add(
        rawType,
        'callout-degraded-to-quote',
        'Callouts have no canonical block; rendered as a quote.',
      );
    } else {
      drops.add(
        rawType,
        'legacy-type-alias-applied',
        `Legacy block name "${rawType}" was written as "${mapped}".`,
      );
    }
    type = mapped;
  }

  if (rawType !== type && !['callout'].includes(rawType)) {
    // already recorded above
  } else if (!isCanonical(type) && rawType !== 'callout') {
    drops.add(
      rawType,
      'unknown-type-degraded-to-paragraph',
      `Unrecognised block type "${rawType}" was written as a paragraph.`,
    );
    type = 'paragraph';
  }

  const inline = renderInline(block.content, drops, rawType);
  const kids = serialiseChildren(block.children, drops, depth);

  let out: string;
  try {
    out = renderByType(type, inline, props, drops, rawType);
  } catch {
    // Per the never-throw contract, any failure degrades to plain text.
    drops.add(
      rawType,
      'block-serialisation-failed',
      `A "${rawType}" block could not be formatted and was written as plain text.`,
    );
    out = inline;
  }

  if (kids) out = out ? `${out}\n\n${kids}` : kids;
  return out;
}

function isCanonical(t: string): boolean {
  return (
    [
      'paragraph', 'heading', 'bulletListItem', 'numberedListItem',
      'checkListItem', 'toggleListItem', 'codeBlock', 'quote', 'table',
      'image', 'file', 'divider', 'pageBreak', 'tableOfContent',
      'column', 'columnList', 'pageContent',
    ] as string[]
  ).includes(t);
}

function renderByType(
  type: string,
  inline: string,
  props: Record<string, unknown>,
  drops: DropLog,
  rawType: string,
): string {
  switch (type) {
    case 'heading': {
      const level = clampLevel(props.level);
      return `${'#'.repeat(level)} ${inline}`;
    }
    case 'bulletListItem':
      return `- ${inline}`;
    case 'numberedListItem':
      return `1. ${inline}`;
    case 'checkListItem': {
      const checked = props.checked === true;
      return `- [${checked ? 'x' : ' '}] ${inline}`;
    }
    case 'toggleListItem':
      return `- ${inline}`;
    case 'codeBlock': {
      const lang = typeof props.language === 'string' && props.language !== 'plaintext'
        ? props.language
        : '';
      const body = unescapeMd(inline);
      return '```' + lang + '\n' + body + '\n```';
    }
    case 'quote':
      return inline
        .split('\n')
        .map((l) => `> ${l}`)
        .join('\n');
    case 'divider':
    case 'pageBreak':
      return '---';
    case 'image': {
      const url = typeof props.url === 'string' ? props.url : '';
      if (!url) {
        drops.add(
          rawType,
          'image-without-url',
          'An image had no URL and was written as its alt text.',
        );
        return inline || '';
      }
      const alt = typeof props.name === 'string' && props.name
        ? props.name
        : (typeof props.caption === 'string' ? props.caption : '');
      return `![${alt}](${url})`;
    }
    case 'file': {
      const url = typeof props.url === 'string' ? props.url : '';
      const name = typeof props.name === 'string' ? props.name : 'file';
      if (!url) {
        drops.add(
          rawType,
          'image-without-url',
          'A file had no URL and was written as its name.',
        );
        return name;
      }
      return `[${name}](${url})`;
    }
    case 'tableOfContent':
      return ''; // TOC is a view concern, not content
    case 'column':
    case 'columnList':
    case 'pageContent':
      return ''; // layout nodes carry no inline text
    case 'paragraph':
    default:
      return inline;
  }
}

function clampLevel(v: unknown): number {
  const n = typeof v === 'number' ? Math.round(v) : 1;
  return Math.min(6, Math.max(1, n));
}

function unescapeMd(s: string): string {
  // Code fences must not carry backslash escapes into the body.
  return s.replace(/\\([\\`*_[\]])/g, '$1');
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Serialise Block[] to canonical markdown. NEVER throws.
 *
 * @param blocks Any array-shaped input; non-arrays degrade to empty.
 * @returns `{ markdown, drops }`. markdown is '' only for empty input.
 */
export function serializeBlocksToMarkdown(blocks: unknown): SerializeResult {
  const drops = new DropLog();
  let markdown = '';

  try {
    if (!Array.isArray(blocks) || blocks.length === 0) {
      return { markdown: '', drops: [] };
    }
    const rendered = (blocks as BlockLike[])
      .map((b) => serialiseBlock(b, drops, 0))
      .filter((s) => s.trim().length > 0);
    markdown = rendered.join('\n\n');
  } catch {
    // Absolute last resort. Something in the input was pathological.
    // Fall back to a text scrape so no content is ever lost.
    try {
      markdown = (blocks as any[])
        .map((b) => scrapeText(b))
        .filter((s) => s.length > 0)
        .join('\n\n');
    } catch {
      markdown = '';
    }
    drops.add(
      '*',
      'block-serialisation-failed',
      'Some blocks could not be converted and were written as plain text.',
    );
  }

  // Never return empty for non-empty input.
  if (markdown.trim().length === 0 && Array.isArray(blocks) && blocks.length > 0) {
    markdown = scrapeText(blocks);
  }

  return { markdown, drops: drops.list() };
}

function scrapeText(node: any, acc: string[] = []): string {
  if (node == null) return acc.join('');
  if (typeof node === 'string') {
    acc.push(node);
    return acc.join('');
  }
  if (Array.isArray(node)) {
    for (const n of node) scrapeText(n, acc);
    return acc.join('');
  }
  if (typeof node === 'object') {
    if (typeof node.text === 'string') acc.push(node.text);
    if (node.content != null) scrapeText(node.content, acc);
    if (Array.isArray(node.children)) for (const c of node.children) scrapeText(c, acc);
  }
  return acc.join('');
}

/** True when the conversion was lossless. */
export function isLossless(result: SerializeResult): boolean {
  return result.drops.length === 0;
}

/** Build the sidecar to persist alongside the markdown. */
export function toMarkdownSidecar(
  result: SerializeResult,
  blockCount: number,
): MarkdownSidecar {
  return {
    version: 1,
    markdown: result.markdown,
    drops: result.drops.length > 0 ? result.drops : null,
    blockCount,
    serialisedAt: new Date().toISOString(),
  };
}

/**
 * Inverse of {@link toMarkdownSidecar} — read a sidecar back.
 * Tolerates a bare markdown string (legacy sidecar shape).
 */
export function fromMarkdownSidecar(
  raw: unknown,
): { markdown: string; drops: Drop[] } | null {
  if (raw == null) return null;
  if (typeof raw === 'string') return { markdown: raw, drops: [] };
  if (typeof raw !== 'object') return null;
  const o = raw as Partial<MarkdownSidecar>;
  if (typeof o.markdown !== 'string') return null;
  return { markdown: o.markdown, drops: Array.isArray(o.drops) ? o.drops : [] };
}
